<template>
  <div class="reset-password-container">
    <!-- Animated background -->
    <div class="background-image"></div>
    
    <!-- Loading Overlay -->
    <transition name="loading-overlay">
      <div v-if="isLoading" class="loading-overlay">
        <div class="loading-card">
          <div class="loading-animation">
            <div class="loading-ring">
              <div class="ring"></div>
              <div class="ring"></div>
              <div class="ring"></div>
              <div class="loading-logo">M</div>
            </div>
          </div>
          <div class="loading-text">
            <h3>{{ loadingMessage }}</h3>
            <div class="loading-dots">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <div class="reset-content" :class="{ 'content-loaded': isContentLoaded }">
      <div class="reset-card">
        <div class="reset-header">
          <transition name="fade-down" appear>
            <h1>Reset Password</h1>
          </transition>
          <transition name="fade-up" appear>
            <p v-if="!isTokenValidated && !passwordResetSuccess">
              Enter your email or username to receive a password reset link
            </p>
            <p v-else-if="isTokenValidated && !passwordResetSuccess">
              Create your new password
            </p>
            <p v-else-if="passwordResetSuccess">
              Password reset successful!
            </p>
          </transition>
        </div>

        <!-- Step 1: Request Password Reset -->
        <transition name="slide-fade">
          <b-form v-if="!isTokenValidated && !passwordResetSuccess" @submit="requestReset" class="reset-form">
            <b-form-group
              id="username-group"
              label="Email or Username:"
              label-for="username"
              align="left"
            >
              <b-form-input
                id="username"
                v-model="resetRequest.username"
                type="text"
                required
                placeholder="Enter your email or username"
                class="form-input"
                :disabled="isLoading"
                autocomplete="off"
              ></b-form-input>
            </b-form-group>

            <transition name="button-pulse">
              <b-button 
                :disabled="isLoading || !resetRequest.username" 
                type="submit" 
                variant="primary" 
                class="reset-button"
              >
                <span v-if="!isLoading">Send Reset Link</span>
                <span v-else class="button-loader">
                  <span class="loader-dot"></span>
                  <span class="loader-dot"></span>
                  <span class="loader-dot"></span>
                </span>
              </b-button>
            </transition>

            <div class="form-footer">
              <router-link to="/login" class="back-link">
                ← Back to Login
              </router-link>
            </div>
          </b-form>
        </transition>

        <!-- Step 2: Set New Password -->
        <transition name="slide-fade">
          <b-form v-if="isTokenValidated && !passwordResetSuccess" @submit="resetPassword" class="reset-form">
            <b-form-group
              id="password-group"
              label="New Password:"
              label-for="new-password"
              align="left"
            >
              <b-form-input
                id="new-password"
                v-model="resetData.new_password"
                type="password"
                required
                placeholder="Enter new password (min. 8 characters)"
                class="form-input"
                :disabled="isLoading"
                :state="passwordStrength"
              ></b-form-input>
              <b-form-text v-if="resetData.new_password && resetData.new_password.length < 8" class="text-danger">
                Password must be at least 8 characters
              </b-form-text>
              <div v-if="resetData.new_password" class="password-strength">
                <div class="strength-bar" :class="getStrengthClass"></div>
                <small class="strength-text">{{ getStrengthText }}</small>
              </div>
            </b-form-group>

            <b-form-group
              id="confirm-password-group"
              label="Confirm Password:"
              label-for="confirm-password"
              align="left"
            >
              <b-form-input
                id="confirm-password"
                v-model="resetData.confirm_password"
                type="password"
                required
                placeholder="Confirm your new password"
                class="form-input"
                :disabled="isLoading"
                :state="passwordsMatch"
              ></b-form-input>
              <b-form-text v-if="resetData.confirm_password && !passwordsMatch" class="text-danger">
                Passwords do not match
              </b-form-text>
            </b-form-group>

            <transition name="button-pulse">
              <b-button 
                :disabled="isLoading || !isFormValid" 
                type="submit" 
                variant="primary" 
                class="reset-button"
              >
                <span v-if="!isLoading">Reset Password</span>
                <span v-else class="button-loader">
                  <span class="loader-dot"></span>
                  <span class="loader-dot"></span>
                  <span class="loader-dot"></span>
                </span>
              </b-button>
            </transition>
          </b-form>
        </transition>

        <!-- Step 3: Success Message -->
        <transition name="slide-fade">
          <div v-if="passwordResetSuccess" class="success-container">
            <div class="success-icon">✓</div>
            <h3>Password Reset Successful!</h3>
            <p>{{ successMessage }}</p>
            <router-link to="/login">
              <b-button variant="primary" class="login-redirect-button">
                Go to Login
              </b-button>
            </router-link>
          </div>
        </transition>
      </div>
    </div>

    <!-- Toast Notifications -->
    <transition name="toast-slide">
      <div v-if="showToast" class="custom-toast" :class="toastType">
        <div class="toast-icon">{{ toastIcon }}</div>
        <div class="toast-content">
          <div class="toast-title">{{ toastTitle }}</div>
          <div class="toast-message">{{ toastMessage }}</div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Requestor from '@/utilities/Requestor'

export default {
  name: "ResetPassword",
  data() {
    return {
      resetRequest: {
        username: ''
      },
      resetData: {
        new_password: '',
        confirm_password: ''
      },
      token: null,
      isTokenValidated: false,
      passwordResetSuccess: false,
      isLoading: false,
      isContentLoaded: false,
      loadingMessage: 'Processing...',
      showToast: false,
      toastMessage: '',
      toastTitle: '',
      toastType: 'success',
      successMessage: ''
    }
  },
  computed: {
    passwordStrength() {
      if (!this.resetData.new_password) return null
      if (this.resetData.new_password.length < 8) return false
      return true
    },
    getStrengthClass() {
      const password = this.resetData.new_password
      if (!password) return ''
      let strength = 0
      if (password.length >= 8) strength++
      if (password.match(/[a-z]/)) strength++
      if (password.match(/[A-Z]/)) strength++
      if (password.match(/[0-9]/)) strength++
      if (password.match(/[^a-zA-Z0-9]/)) strength++
      
      if (strength <= 2) return 'strength-weak'
      if (strength <= 4) return 'strength-medium'
      return 'strength-strong'
    },
    getStrengthText() {
      const password = this.resetData.new_password
      if (!password) return ''
      let strength = 0
      if (password.length >= 8) strength++
      if (password.match(/[a-z]/)) strength++
      if (password.match(/[A-Z]/)) strength++
      if (password.match(/[0-9]/)) strength++
      if (password.match(/[^a-zA-Z0-9]/)) strength++
      
      if (strength <= 2) return 'Weak password'
      if (strength <= 4) return 'Medium password'
      return 'Strong password'
    },
    passwordsMatch() {
      if (!this.resetData.confirm_password) return null
      return this.resetData.new_password === this.resetData.confirm_password
    },
    isFormValid() {
      return this.resetData.new_password.length >= 8 && 
             this.passwordsMatch === true
    },
    toastIcon() {
      return this.toastType === 'success' ? '✓' : '✗'
    }
  },
  mounted() {
    setTimeout(() => {
      this.isContentLoaded = true
    }, 100)
    
    // Get token from URL
    this.token = this.$route.query.token
    if (this.token) {
      this.validateToken()
    }
  },
  methods: {
    showToastMessage(message, title = 'Success', type = 'success') {
      this.toastMessage = message
      this.toastTitle = title
      this.toastType = type
      this.showToast = true
      setTimeout(() => {
        this.showToast = false
      }, 5000)
    },
    
    validateToken() {
      this.isLoading = true
      this.loadingMessage = 'Validating reset link...'
      
      let path = "/api/user/verify-reset-token/"
      let data = { token: this.token }
      
      let s = (response) => {
        this.isLoading = false
        if (response.data.valid) {
          this.isTokenValidated = true
          this.showToastMessage('Reset token validated! Please enter your new password.', 'Success', 'success')
        } else {
          this.showToastMessage('Invalid or expired reset link. Please request a new one.', 'Error', 'error')
          setTimeout(() => {
            this.$router.push('/login')
          }, 3000)
        }
      }
      
      let f = (error) => {
        this.isLoading = false
        let errorMsg = error.response.data.error || 'Failed to validate reset token'
        this.showToastMessage(errorMsg, 'Error', 'error')
        setTimeout(() => {
          this.$router.push('/login')
        }, 3000)
      }
      
      Requestor.postSync(path, data, s, f)
    },
    
    requestReset(e) {
      e.preventDefault()
      this.isLoading = true
      this.loadingMessage = 'Sending reset link...'
      
      let path = "/api/user/request-password-reset/"
      let data = { username: this.resetRequest.username }
      
      let s = (response) => {
        this.isLoading = false
        this.showToastMessage(
          response.data.message || 'Password reset link has been sent to your email.',
          'Email Sent',
          'success'
        )
        this.resetRequest.username = ''
      }
      
      let f = (error) => {
        this.isLoading = false
        let errorMsg = error.response.data.error || 'Failed to send reset link'
        this.showToastMessage(errorMsg, 'Error', 'error')
      }
      
      Requestor.postSync(path, data, s, f)
    },
    
    resetPassword(e) {
      e.preventDefault()
      
      if (!this.isFormValid) {
        this.showToastMessage('Please ensure your passwords are valid and match.', 'Validation Error', 'error')
        return
      }
      
      this.isLoading = true
      this.loadingMessage = 'Resetting password...'
      
      let path = "/api/user/reset-password/"
      let data = {
        token: this.token,
        new_password: this.resetData.new_password,
        confirm_password: this.resetData.confirm_password
      }
      
      let s = (response) => {
        this.isLoading = false
        if (response.data.success) {
          this.passwordResetSuccess = true
          this.successMessage = response.data.message || 'Your password has been reset successfully!'
          this.showToastMessage('Password reset successful! Redirecting to login...', 'Success', 'success')
          setTimeout(() => {
            this.$router.push('/login')
          }, 3000)
        } else {
          this.showToastMessage(response.data.error || 'Failed to reset password', 'Error', 'error')
        }
      }
      
      let f = (error) => {
        this.isLoading = false
        let errorMsg = error.response.data.error || 'Failed to reset password'
        this.showToastMessage(errorMsg, 'Error', 'error')
      }
      
      Requestor.postSync(path, data, s, f)
    }
  }
}
</script>

<style scoped>
.reset-password-container {
  position: relative;
  min-height: 100vh;
  width: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.background-image {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  filter: blur(8px);
  opacity: 0.8;
  z-index: -1;
  animation: backgroundZoom 20s ease-in-out infinite;
}

@keyframes backgroundZoom {
  0%, 100% { transform: scale(1.05); }
  50% { transform: scale(1.1); }
}

.reset-content {
  width: 100%;
  max-width: 500px;
  margin: 20px;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.reset-content.content-loaded {
  opacity: 1;
  transform: translateY(0);
}

.reset-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

.reset-header {
  text-align: center;
  margin-bottom: 40px;
}

.reset-header h1 {
  font-size: 2rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 10px;
}

.reset-header p {
  color: #718096;
  font-size: 0.95rem;
}

.reset-form {
  margin-top: 20px;
}

.form-input {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.form-input:focus {
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  transform: translateY(-2px);
}

.password-strength {
  margin-top: 8px;
}

.strength-bar {
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  transition: all 0.3s ease;
  width: 100%;
  margin-bottom: 4px;
}

.strength-weak {
  background: #e53e3e;
  width: 33%;
}

.strength-medium {
  background: #ed8936;
  width: 66%;
}

.strength-strong {
  background: #48bb78;
  width: 100%;
}

.strength-text {
  font-size: 0.75rem;
  color: #718096;
}

.reset-button {
  width: 100%;
  padding: 14px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 10px;
  background: linear-gradient(135deg, #4299e1, #3182ce);
  border: none;
  transition: all 0.3s ease;
  margin-top: 20px;
}

.reset-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(66, 153, 225, 0.3);
}

.reset-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.form-footer {
  text-align: center;
  margin-top: 20px;
}

.back-link {
  color: #4299e1;
  text-decoration: none;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.back-link:hover {
  color: #3182ce;
  text-decoration: underline;
}

.success-container {
  text-align: center;
  padding: 20px 0;
}

.success-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #48bb78, #38a169);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 48px;
  color: white;
  animation: successPop 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes successPop {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.login-redirect-button {
  margin-top: 20px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 10px;
  background: linear-gradient(135deg, #4299e1, #3182ce);
  border: none;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(20px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.loading-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(245, 245, 245, 0.95));
  border-radius: 24px;
  padding: 48px 56px;
  text-align: center;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  animation: cardPop 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  max-width: 400px;
  width: 90%;
}

@keyframes cardPop {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.loading-animation {
  margin-bottom: 32px;
}

.loading-ring {
  position: relative;
  width: 100px;
  height: 100px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 4px solid transparent;
  border-radius: 50%;
  animation: ringRotate 1.5s linear infinite;
}

.ring:nth-child(1) {
  border-top-color: #4299e1;
  border-left-color: #4299e1;
  animation-duration: 1s;
}

.ring:nth-child(2) {
  border-right-color: #48bb78;
  border-bottom-color: #48bb78;
  width: 80%;
  height: 80%;
  top: 10%;
  left: 10%;
  animation-duration: 1.2s;
  animation-direction: reverse;
}

.ring:nth-child(3) {
  border-left-color: #ed8936;
  border-bottom-color: #ed8936;
  width: 60%;
  height: 60%;
  top: 20%;
  left: 20%;
  animation-duration: 0.8s;
}

@keyframes ringRotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-logo {
  font-size: 32px;
  font-weight: bold;
  color: #2d3748;
  animation: logoPulse 1.5s ease-in-out infinite;
  z-index: 1;
}

@keyframes logoPulse {
  0%, 100% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.1); opacity: 1; }
}

.loading-text {
  margin-bottom: 24px;
}

.loading-text h3 {
  color: #2d3748;
  font-size: 1.3rem;
  margin-bottom: 8px;
  display: inline-block;
}

.loading-dots {
  display: inline-flex;
  margin-left: 8px;
}

.loading-dots .dot {
  width: 6px;
  height: 6px;
  background: #4299e1;
  border-radius: 50%;
  margin: 0 2px;
  animation: dotBounce 1.4s ease-in-out infinite;
}

.loading-dots .dot:nth-child(1) { animation-delay: 0s; }
.loading-dots .dot:nth-child(2) { animation-delay: 0.2s; }
.loading-dots .dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes dotBounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-8px); }
}

.button-loader {
  display: inline-flex;
  gap: 4px;
  align-items: center;
}

.loader-dot {
  width: 8px;
  height: 8px;
  background: white;
  border-radius: 50%;
  animation: bounce 1.4s ease-in-out infinite;
}

.loader-dot:nth-child(1) { animation-delay: 0s; }
.loader-dot:nth-child(2) { animation-delay: 0.2s; }
.loader-dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes bounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-6px); }
}

.custom-toast {
  position: fixed;
  bottom: 30px;
  right: 30px;
  background: white;
  padding: 16px 24px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  z-index: 2000;
  min-width: 280px;
  animation: slideInRight 0.3s ease;
}

.custom-toast.success {
  border-left: 4px solid #48bb78;
}

.custom-toast.success .toast-icon {
  background: #48bb78;
  color: white;
}

.custom-toast.error {
  border-left: 4px solid #e53e3e;
}

.custom-toast.error .toast-icon {
  background: #e53e3e;
  color: white;
}

.toast-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.toast-content {
  flex: 1;
}

.toast-title {
  font-weight: bold;
  margin-bottom: 4px;
  color: #2d3748;
}

.toast-message {
  font-size: 0.9rem;
  color: #718096;
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(100px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.fade-down-enter-active,
.fade-up-enter-active,
.slide-fade-enter-active,
.button-pulse-enter-active {
  transition: all 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.fade-down-enter,
.fade-down-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}

.fade-up-enter,
.fade-up-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateX(-50px);
}

.button-pulse-enter {
  transform: scale(0.95);
  opacity: 0;
}

@media (max-width: 768px) {
  .reset-card {
    padding: 30px 20px;
  }
  
  .reset-header h1 {
    font-size: 1.5rem;
  }
  
  .custom-toast {
    bottom: 20px;
    right: 20px;
    left: 20px;
    min-width: auto;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/importables/ResetPassword.vue